import React, { useCallback } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY, DOCK_TAB_WIDTH, Z_INDEX } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER, TRANSITION_HOVER } from '../../utils/webStyles';

interface DockTabProps {
  /** Which side the tab sits on and slides out from (matches DockedPanel's `side`). */
  side: 'left' | 'right';
  /** Name of the collapsed panel, read top-to-bottom beside the chevron. */
  label: string;
  /** Called after setPanelNewest(side) — the panel's own re-expand logic. */
  onExpand?: () => void;
  testID?: string;
}

/**
 * The 40px collapsed state a DockedPanel renders into when `collapsed` is
 * true (panel-system.md phase 0). Pressing it makes its side the newest —
 * the one resolvePanelLayout never folds — before handing off to the
 * caller's own expand logic, so the panel budget agrees with what the user
 * just asked for instead of immediately re-folding it.
 */
export function DockTab({ side, label, onExpand, testID }: DockTabProps) {
  const { colors } = useTheme();
  const setPanelNewest = useUIStore((s) => s.setPanelNewest);
  const { hovered, hoverProps } = useWebHover();
  const Chevron = side === 'left' ? ChevronRight : ChevronLeft;

  const handlePress = useCallback(() => {
    setPanelNewest(side);
    onExpand?.();
  }, [setPanelNewest, side, onExpand]);

  return (
    <Pressable
      {...hoverProps}
      testID={testID ?? `dock-tab-${side}`}
      onPress={handlePress}
      accessibilityRole="button"
      aria-label={`Expand ${label}`}
      style={[
        styles.tab,
        side === 'left' ? styles.tabLeft : styles.tabRight,
        {
          width: DOCK_TAB_WIDTH,
          backgroundColor: hovered ? colors.hoverSurface : colors.surfaceElevated,
          // The map-facing edge is the right border on a left tab, the left
          // border on a right tab — same "edge facing the map" rule as
          // DockedPanel's own border.
          [side === 'left' ? 'borderRightColor' : 'borderLeftColor']: colors.border,
        },
        TRANSITION_HOVER,
        CURSOR_POINTER,
      ]}>
      <View style={styles.content}>
        <Chevron size={16} color={colors.textSecondary} />
        <Text style={[styles.label, { color: colors.textSecondary }]} numberOfLines={1}>
          {label}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  tab: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: Z_INDEX.dockedPanel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabLeft: {
    left: 0,
    borderRightWidth: 1,
  },
  tabRight: {
    right: 0,
    borderLeftWidth: 1,
  },
  content: {
    alignItems: 'center',
    gap: SPACING.sm,
  },
  label: {
    ...TYPOGRAPHY.labelSmall,
    // Read top-to-bottom in the 40px column, same technique as the app's
    // other rotated-square notch (MapPostHoverCard) rather than a real
    // vertical-writing-mode, which RN has no cross-platform support for.
    transform: [{ rotate: '-90deg' }],
    width: 120,
    textAlign: 'center',
  },
});
